import { useFormatDateTime } from "@bookingcom/flights-core";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { formattingUtils } from "@flights/web-api-utils-universal";
import useAirlineLogosGrouped from "../../../../../../hooks/useAirlineLogosGrouped.js";
import { useMemo } from "react";
import { trackWithDefaultStage } from "@flights/et-universal";
import useProfiler from "../../../../../../hooks/useProfiler.js";
export const useCardSegmentsCondensedController = (segments) => {
    const { selectFlightCarriers: segmentsCarriers, airlinesWithGridPosition } = useAirlineLogosGrouped(segments);
    const i18n = useI18n();
    const { formats } = useFormatDateTime(i18n);
    const { isDesktop } = useProfiler();
    const extractAirlineCode = (logoUrl) => {
        const code = logoUrl.split("/").pop()?.replace(".png", "");
        return code;
    };
    const logoCodes = useMemo(() => {
        const codes = [];
        segments.forEach((_, index) => {
            const _logos = new Set();
            airlinesWithGridPosition[index].forEach((p) => {
                const code = extractAirlineCode(p.logo);
                if (code) {
                    _logos.add(code);
                }
            });
            codes.push([..._logos].slice(0, 4));
        });
        return codes;
    }, [segments, airlinesWithGridPosition]);
    const getBadgeText = (stops) => {
        if (!stops || stops === 0) {
            return i18n.trans(t("flights_filter_stops_none"));
        }
        else {
            return i18n.trans(t("flights_route_num_stops", {
                num_exception: stops,
                variables: { num_stops: stops }
            }));
        }
    };
    const getBadgeVariant = (stops) => {
        if (!stops)
            return "constructive";
        return "outline";
    };
    const getCarrierText = (carrier) => {
        if (!carrier.carrierData)
            return "";
        if (carrier.operatingCarrierDisclosureText) {
            return i18n.trans(t("flights_sr_marketing_operating_airline", {
                variables: {
                    marketing_carrier: carrier.carrierData.name,
                    operating_carrier: carrier.operatingCarrierDisclosureText
                }
            }));
        }
        else if (carrier.operatingCarrierData) {
            return i18n.trans(t("flights_sr_marketing_operating_airline", {
                variables: {
                    marketing_carrier: carrier.carrierData.name,
                    operating_carrier: carrier.operatingCarrierData.name
                }
            }));
        }
        else {
            return carrier.carrierData.name;
        }
    };
    const getCarriersText = (flightCarriers) => {
        const uniqueFlightCarriers = [...new Set(flightCarriers.flat())];
        return uniqueFlightCarriers
            .map((carrier) => {
            return getCarrierText(carrier);
        })
            .join(", ");
    };
    const generateSegmentData = () => {
        return segments.map((segment, index) => ({
            from: {
                date: formats.flightDate(segment.departureTime),
                time: formats.flightTime(segment.departureTime),
                highlightIata: segment.showWarningOriginAirport,
                highlightCallout: segment.highlightOriginAirport && !!trackWithDefaultStage("flights_funnel_sr_airport_highlight_web", 1),
                iataTooltipText: isDesktop() && trackWithDefaultStage("flights_funnel_sr_airport_tooltip_desktop", 1)
                    ? segment.departureAirport.name
                    : undefined,
                iataTooltipTextA11y: isDesktop() && trackWithDefaultStage("flights_funnel_sr_airport_tooltip_desktop", 1)
                    ? i18n.trans(t("a11y_flights_sr_iata_tooltip"))
                    : undefined,
                iata: segment.departureAirport.code
            },
            to: {
                date: formats.flightDate(segment.arrivalTime),
                time: formats.flightTime(segment.arrivalTime),
                highlightIata: segment.showWarningDestinationAirport,
                highlightCallout: segment.highlightDestinationAirport && !!trackWithDefaultStage("flights_funnel_sr_airport_highlight_web", 1),
                iataTooltipText: isDesktop() && trackWithDefaultStage("flights_funnel_sr_airport_tooltip_desktop", 1)
                    ? segment.arrivalAirport.name
                    : undefined,
                iataTooltipTextA11y: isDesktop() && trackWithDefaultStage("flights_funnel_sr_airport_tooltip_desktop", 1)
                    ? i18n.trans(t("a11y_flights_sr_iata_tooltip"))
                    : undefined,
                iata: segment.arrivalAirport.code
            },
            stops: {
                stops: getBadgeText(segment.legs.length - 1),
                duration: formattingUtils.flightDuration(segment.totalTime / 60, i18n),
                badgeVariant: getBadgeVariant(segment.legs.length - 1)
            },
            airlineLogos: { codes: logoCodes[index] },
            size: "medium"
        }));
    };
    return {
        segmentsData: generateSegmentData(),
        carrierText: getCarriersText(segmentsCarriers)
    };
};
